import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  Terminal,
  Plus,
  Edit3,
  Trash2,
  RefreshCw,
  Copy,
  Eye,
  Search,
  Filter,
  FileText,
  Link2,
  X,
  Save,
  Check,
  AlertCircle,
  Loader2,
  ChevronDown,
  Shield,
  Archive,
  MoreVertical,
  Square,
  CheckSquare,
  MinusSquare,
  Rocket,
  ArrowDownCircle,
  AlertTriangle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// ============================================
// NEW METADATA-BASED CONSTANTS
// ============================================

// Category definitions (for classification/filtering)
const CATEGORY_OPTIONS = [
  { value: 'all', label: 'All Categories' },
  { value: 'letter_generation', label: 'Letter Generation' },
  { value: 'credit_analysis', label: 'Credit Analysis' },
  { value: 'parser_training', label: 'Parser Training' },
  { value: 'decision_training', label: 'Decision Training' },
  { value: 'ai_knowledge_base', label: 'AI Knowledge Base' },
  { value: 'prompt_reference', label: 'Prompt Reference' },
];

// Family definitions (for dispute type routing)
const FAMILY_OPTIONS = [
  { value: 'all', label: 'All Families' },
  { value: 'isolated', label: 'Isolated' },
  { value: 'structural', label: 'Structural' },
  { value: 'recovery', label: 'Recovery' },
  { value: 'structural_recovery_combo', label: 'Structural + Recovery Combo' },
  { value: 'data_breach', label: 'Data Breach' },
  { value: 'personal_information', label: 'Personal Information' },
  { value: 'inquiry', label: 'Inquiry' },
  { value: 'metro2', label: 'Metro 2' },
  { value: 'alternate_bureau', label: 'Alternate Bureau' },
];

// Round definitions
const ROUND_OPTIONS = [
  { value: 'all', label: 'All Rounds' },
  { value: 'Round 1', label: 'Round 1' },
  { value: 'Round 2', label: 'Round 2' },
  { value: 'Round 3', label: 'Round 3' },
  { value: 'All Rounds', label: 'All Rounds (Universal)' },
];

// Common method values (for searchable select/autocomplete)
const METHOD_SUGGESTIONS = [
  'round1_isolated',
  'round1_structural',
  'round1_recovery',
  'round1_combo',
  'round2_isolated',
  'round2_structural',
  'round2_recovery',
  'round2_combo',
  'round3_isolated_prelitigation',
  'round3_structural_prelitigation',
  'round3_recovery_prelitigation',
  'round3_combo_prelitigation',
  'personal_information_dispute',
  'data_breach_noncompliance',
  'collection_verification',
  'inquiry_dispute',
  'metro2_structural_analysis',
];

// Status definitions
const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'live', label: 'Live' },
  { value: 'draft', label: 'Draft' },
  { value: 'archived', label: 'Archived' },
];

// Source definitions (for "More Filters")
const SOURCE_OPTIONS = [
  { value: 'all', label: 'All Sources' },
  { value: 'native', label: 'Native Prompts' },
  { value: 'intake', label: 'From Intake' },
];

// Legacy task type mapping (for backward compatibility)
const TASK_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'credit_analysis', label: 'Credit Analysis' },
  { value: 'dispute_reasoning', label: 'Dispute Reasoning' },
  { value: 'letter_generation', label: 'Letter Generation' },
  { value: 'account_classification', label: 'Account Classification' },
  { value: 'funding_readiness', label: 'Funding Readiness' },
  { value: 'next_actions', label: 'Next Actions' },
  { value: 'prompt_reference', label: 'Prompt Reference' },
];

// Helper functions for display
const getCategoryLabel = (value) => {
  const found = CATEGORY_OPTIONS.find(c => c.value === value);
  return found ? found.label : value || 'Letter Generation';
};

const getFamilyLabel = (value) => {
  const found = FAMILY_OPTIONS.find(f => f.value === value);
  return found ? found.label : value || 'Isolated';
};

const getRoundLabel = (value) => {
  if (!value) return 'Round 1';
  // Handle both string and number
  if (typeof value === 'number') {
    return `Round ${value}`;
  }
  const found = ROUND_OPTIONS.find(r => r.value === value);
  return found ? found.label : value;
};

// Prompt Card Component - Updated with Metadata Badges
const PromptCard = ({ prompt, onView, onEdit, onCopy, onDelete, isSelected, onSelect }) => {
  const isIntake = prompt.source === 'intake';
  const isLive = prompt.status === 'live' || prompt.is_active;
  
  // Get preview text (truncated) - show clean content without metadata
  const getPreviewText = (text, maxLength = 120) => {
    if (!text) return 'No content available';
    // Remove any metadata headers that might have leaked through
    const cleaned = text
      .replace(/PROMPT\s*NAME:.*?\n/gi, '')
      .replace(/CATEGORY:.*?\n/gi, '')
      .replace(/FAMILY:.*?\n/gi, '')
      .replace(/METHOD:.*?\n/gi, '')
      .replace(/ROUND:.*?\n/gi, '')
      .replace(/PROMPT\s*BODY:\s*/gi, '')
      .replace(/\n+/g, ' ')
      .trim();
    return cleaned.length > maxLength ? cleaned.substring(0, maxLength) + '...' : cleaned;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'live': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'draft': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'archived': return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  // Family color coding
  const getFamilyColor = (family) => {
    switch (family) {
      case 'structural': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'recovery': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'structural_recovery_combo': return 'bg-pink-500/20 text-pink-400 border-pink-500/30';
      case 'data_breach': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'personal_information': return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
      case 'inquiry': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'metro2': return 'bg-teal-500/20 text-teal-400 border-teal-500/30';
      case 'isolated': 
      default: return 'bg-slate-500/20 text-slate-300 border-slate-500/30';
    }
  };

  return (
    <div 
      className={`bg-stone-800/50 border rounded-lg p-4 hover:bg-stone-800/70 transition-all ${
        isLive ? 'border-l-4 border-l-emerald-500 border-slate-700' : 'border-slate-700'
      } ${isIntake ? 'border-r-4 border-r-amber-500' : ''} ${isSelected ? 'ring-2 ring-amber-500/50' : ''}`}
      data-testid={`prompt-card-${prompt.id}`}
    >
      {/* Header Row with Checkbox */}
      <div className="flex items-start gap-3 mb-2">
        {/* Checkbox for selection */}
        <div className="pt-1">
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onSelect(prompt.id)}
            className="border-slate-500 data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
            data-testid={`select-prompt-${prompt.id}`}
          />
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-semibold text-base truncate" title={prompt.name}>
            {prompt.name || 'Untitled Prompt'}
          </h3>
          
          {/* Status & Version Row */}
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-xs px-2 py-0.5 rounded border ${getStatusColor(prompt.status)}`}>
              {prompt.status || 'draft'}
            </span>
            <span className="text-xs text-slate-500">
              v{prompt.version || 1}
            </span>
            {isIntake && (
              <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30 flex items-center gap-1">
                <Link2 size={10} />
                Intake
              </span>
            )}
          </div>
        </div>
      </div>

      {/* METADATA BADGES ROW - New Design */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {/* Category Badge */}
        <span className="text-xs px-2 py-0.5 rounded bg-slate-700/50 text-slate-300 border border-slate-600/50">
          {getCategoryLabel(prompt.category)}
        </span>
        
        {/* Family Badge - Color coded */}
        <span className={`text-xs px-2 py-0.5 rounded border ${getFamilyColor(prompt.family)}`}>
          {getFamilyLabel(prompt.family)}
        </span>
        
        {/* Round Badge */}
        <span className="text-xs px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-400 border border-indigo-500/30">
          {getRoundLabel(prompt.round || prompt.round_number)}
        </span>
        
        {/* Method Badge (if set) */}
        {prompt.method && (
          <span className="text-xs px-2 py-0.5 rounded bg-stone-700/50 text-slate-400 border border-stone-600/50 truncate max-w-[120px]" title={prompt.method}>
            {prompt.method}
          </span>
        )}
      </div>

      {/* Intake Source Context */}
      {isIntake && prompt.source_intake_title && (
        <div className="text-xs text-amber-400/80 mb-2 flex items-center gap-1 bg-amber-500/10 px-2 py-1 rounded">
          <FileText size={12} />
          <span className="truncate">Linked: {prompt.source_intake_title}</span>
        </div>
      )}

      {/* Preview Text - Clean content only */}
      <p className="text-sm text-slate-400 leading-relaxed mb-3 line-clamp-2">
        {getPreviewText(prompt.content || prompt.prompt_text)}
      </p>

      {/* Footer Row */}
      <div className="flex items-center justify-between pt-2 border-t border-slate-700/50">
        <div className="text-xs text-slate-500">
          {prompt.created_at && new Date(prompt.created_at).toLocaleDateString()}
          {prompt.word_count && ` · ${prompt.word_count} words`}
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onView(prompt)}
            className="h-8 px-2 text-slate-400 hover:text-white hover:bg-slate-700"
            data-testid={`view-prompt-${prompt.id}`}
          >
            <Eye size={14} className="mr-1" />
            View
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onCopy(prompt)}
            className="h-8 px-2 text-slate-400 hover:text-white hover:bg-slate-700"
            data-testid={`copy-prompt-${prompt.id}`}
          >
            <Copy size={14} />
          </Button>
          {!isIntake && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(prompt)}
              className="h-8 px-2 text-slate-400 hover:text-white hover:bg-slate-700"
              data-testid={`edit-prompt-${prompt.id}`}
            >
              <Edit3 size={14} />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(prompt)}
            className="h-8 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
            data-testid={`delete-prompt-${prompt.id}`}
          >
            <Trash2 size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main Component
const PromptManagement = () => {
  const { user, getAuthHeaders } = useAuth();
  
  // Data state
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [counts, setCounts] = useState({ native: 0, intake_derived: 0, total: 0 });
  
  // Filter state - Updated with metadata filters
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [familyFilter, setFamilyFilter] = useState('all');
  const [roundFilter, setRoundFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all'); // Moved to "More Filters"
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  
  // Selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  
  // Bulk action state
  const [bulkModal, setBulkModal] = useState({
    open: false,
    action: null, // 'archive' | 'delete' | 'publish' | 'unpublish'
    processing: false,
    twoFaCode: '',
    reason: '',
    eligibilityInfo: null // For publish actions: { eligible: [], ineligible: [], reasons: {} }
  });
  
  // Modal state
  const [viewModal, setViewModal] = useState({ open: false, prompt: null });
  const [editModal, setEditModal] = useState({ open: false, prompt: null, saving: false });
  const [deleteModal, setDeleteModal] = useState({ 
    open: false, 
    prompt: null, 
    deleting: false,
    reason: '',
    twoFaCode: ''
  });
  const [createModal, setCreateModal] = useState({ open: false, saving: false });
  
  // Edit form state - Updated with metadata fields
  const [editForm, setEditForm] = useState({ 
    name: '', 
    category: 'letter_generation',
    family: 'isolated',
    method: '',
    round: 'Round 1',
    content: '', 
    notes: '' 
  });
  
  // Create form state - Updated with metadata fields
  const [createForm, setCreateForm] = useState({
    name: '',
    category: 'letter_generation',
    family: 'isolated',
    method: '',
    round: 'Round 1',
    content: '',
    notes: ''
  });

  // Fetch prompts
  const fetchPrompts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/admin-ops/prompts?include_intake=true`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      let data = response.data.prompts || [];
      
      // Store counts
      if (response.data.counts) {
        setCounts({
          native: response.data.counts.native || 0,
          intake_derived: response.data.counts.intake_derived || 0,
          total: response.data.total || data.length
        });
      }
      
      setPrompts(data);
    } catch (err) {
      console.error('Failed to fetch prompts:', err);
      setError('Failed to load prompts');
      toast.error('Failed to load prompts');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  // Filter prompts - Updated with metadata-based filtering
  const filteredPrompts = prompts.filter(prompt => {
    // Hide deleted/archived prompts from view
    if (prompt.status === 'deleted' || prompt.status === 'archived') {
      return false;
    }
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesName = (prompt.name || '').toLowerCase().includes(query);
      const matchesContent = (prompt.content || prompt.prompt_text || '').toLowerCase().includes(query);
      const matchesMethod = (prompt.method || '').toLowerCase().includes(query);
      if (!matchesName && !matchesContent && !matchesMethod) return false;
    }
    
    // Category filter (new)
    if (categoryFilter !== 'all') {
      const promptCategory = prompt.category || 'letter_generation';
      if (promptCategory !== categoryFilter) return false;
    }
    
    // Family filter (new)
    if (familyFilter !== 'all') {
      const promptFamily = prompt.family || 'isolated';
      if (promptFamily !== familyFilter) return false;
    }
    
    // Round filter (new)
    if (roundFilter !== 'all') {
      const promptRound = prompt.round || 'Round 1';
      if (promptRound !== roundFilter) return false;
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      if (prompt.status !== statusFilter) return false;
    }
    
    // Source filter (More Filters)
    if (sourceFilter !== 'all') {
      if (prompt.source !== sourceFilter) return false;
    }
    
    return true;
  });

  // Selection helpers - allow selecting ALL prompt types (native and intake)
  const visiblePromptIds = filteredPrompts.map(p => p.id);
  
  const selectedCount = selectedIds.size;
  const allVisibleSelected = visiblePromptIds.length > 0 && 
    visiblePromptIds.every(id => selectedIds.has(id));
  const someVisibleSelected = visiblePromptIds.some(id => selectedIds.has(id));
  
  // Toggle single selection
  const handleToggleSelect = (promptId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(promptId)) {
        next.delete(promptId);
      } else {
        next.add(promptId);
      }
      return next;
    });
  };
  
  // Select/Deselect all visible prompts (both native and intake)
  const handleSelectAll = () => {
    if (allVisibleSelected) {
      // Deselect all visible
      setSelectedIds(prev => {
        const next = new Set(prev);
        visiblePromptIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      // Select all visible prompts
      setSelectedIds(prev => {
        const next = new Set(prev);
        visiblePromptIds.forEach(id => next.add(id));
        return next;
      });
    }
  };
  
  // Clear selection
  const clearSelection = () => {
    setSelectedIds(new Set());
  };
  
  // Get selected prompts status breakdown for smart bulk actions
  const getSelectionStatusBreakdown = () => {
    const breakdown = {
      draft: [],
      live: [],
      archived: [],
      total: selectedIds.size
    };
    
    Array.from(selectedIds).forEach(id => {
      const prompt = prompts.find(p => p.id === id);
      if (prompt) {
        const status = prompt.status || (prompt.is_active ? 'live' : 'draft');
        if (status === 'live' || prompt.is_active) {
          breakdown.live.push(prompt);
        } else if (status === 'archived') {
          breakdown.archived.push(prompt);
        } else {
          breakdown.draft.push(prompt);
        }
      }
    });
    
    return breakdown;
  };
  
  // Check eligibility for publishing
  const checkPublishEligibility = () => {
    const eligible = [];
    const ineligible = [];
    const reasons = {};
    
    Array.from(selectedIds).forEach(id => {
      const prompt = prompts.find(p => p.id === id);
      if (!prompt) return;
      
      const status = prompt.status || (prompt.is_active ? 'live' : 'draft');
      
      // Already live
      if (status === 'live' || prompt.is_active) {
        ineligible.push(prompt);
        reasons[id] = 'Already live';
        return;
      }
      
      // Archived - cannot publish directly
      if (status === 'archived') {
        ineligible.push(prompt);
        reasons[id] = 'Archived - restore first';
        return;
      }
      
      // Check required fields
      const content = prompt.content || prompt.prompt_text || '';
      if (!content || content.trim().length < 10) {
        ineligible.push(prompt);
        reasons[id] = 'Missing or insufficient content';
        return;
      }
      
      if (!prompt.name || prompt.name.trim().length < 3) {
        ineligible.push(prompt);
        reasons[id] = 'Missing or invalid name';
        return;
      }
      
      // Passed all checks
      eligible.push(prompt);
    });
    
    return { eligible, ineligible, reasons };
  };
  
  // Open bulk action modal
  const handleBulkAction = (action) => {
    let eligibilityInfo = null;
    
    // For publish action, calculate eligibility first
    if (action === 'publish') {
      eligibilityInfo = checkPublishEligibility();
      
      if (eligibilityInfo.eligible.length === 0) {
        toast.error('No selected prompts are eligible for publishing');
        return;
      }
    }
    
    // For unpublish action, check if any are live
    if (action === 'unpublish') {
      const breakdown = getSelectionStatusBreakdown();
      if (breakdown.live.length === 0) {
        toast.error('No selected prompts are currently live');
        return;
      }
      eligibilityInfo = {
        eligible: breakdown.live,
        ineligible: [...breakdown.draft, ...breakdown.archived],
        reasons: {}
      };
      breakdown.draft.forEach(p => eligibilityInfo.reasons[p.id] = 'Not currently live');
      breakdown.archived.forEach(p => eligibilityInfo.reasons[p.id] = 'Already archived');
    }
    
    setBulkModal({
      open: true,
      action,
      processing: false,
      twoFaCode: '',
      reason: '',
      eligibilityInfo
    });
  };
  
  // Reset bulk modal
  const resetBulkModal = () => {
    setBulkModal({
      open: false,
      action: null,
      processing: false,
      twoFaCode: '',
      reason: '',
      eligibilityInfo: null
    });
  };
  
  // Execute bulk archive - SOURCE-AWARE ROUTING
  const executeBulkArchive = async () => {
    setBulkModal(prev => ({ ...prev, processing: true }));
    
    // Separate selected prompts by source
    const selectedPromptIds = Array.from(selectedIds);
    const nativeIds = [];
    const intakeIds = [];
    
    for (const id of selectedPromptIds) {
      const prompt = prompts.find(p => p.id === id);
      if (prompt) {
        if (prompt.source === 'intake') {
          intakeIds.push(id);
        } else {
          nativeIds.push(id);
        }
      }
    }
    
    let succeeded = 0;
    let failed = 0;
    
    // Archive native prompts
    for (const promptId of nativeIds) {
      try {
        await axios.delete(
          `${API}/admin-ops/prompts/${promptId}`,
          { headers: getAuthHeaders(), withCredentials: true }
        );
        succeeded++;
      } catch (err) {
        console.error(`Failed to archive native prompt ${promptId}:`, err);
        failed++;
      }
    }
    
    // Archive intake-derived prompts (update status to archived)
    for (const refId of intakeIds) {
      try {
        // Since there's no simple archive endpoint for intake refs, we set status to archived
        await axios.patch(
          `${API}/admin-ops/prompt-references/${refId}/archive`,
          {},
          { headers: getAuthHeaders(), withCredentials: true }
        );
        succeeded++;
      } catch (err) {
        // Fallback: try to delete with a generic reason
        console.error(`Failed to archive intake prompt ${refId}:`, err);
        failed++;
      }
    }
    
    resetBulkModal();
    clearSelection();
    fetchPrompts();
    
    if (failed === 0) {
      toast.success(`Successfully archived ${succeeded} prompt(s)`);
    } else {
      toast.warning(`Archived ${succeeded}, failed ${failed}`);
    }
  };
  
  // Execute bulk delete with 2FA - SOURCE-AWARE ROUTING
  const executeBulkDelete = async () => {
    if (!bulkModal.twoFaCode || bulkModal.twoFaCode.length !== 6) {
      toast.error('Please enter a valid 6-digit 2FA code');
      return;
    }
    
    if (!bulkModal.reason || bulkModal.reason.trim().length < 5) {
      toast.error('Please provide a reason (minimum 5 characters)');
      return;
    }
    
    setBulkModal(prev => ({ ...prev, processing: true }));
    
    // Separate selected prompts by source
    const selectedPromptIds = Array.from(selectedIds);
    const nativeIds = [];
    const intakeIds = [];
    
    for (const id of selectedPromptIds) {
      const prompt = prompts.find(p => p.id === id);
      if (prompt) {
        if (prompt.source === 'intake') {
          intakeIds.push(id);
        } else {
          nativeIds.push(id);
        }
      }
    }
    
    try {
      // First verify 2FA for the batch (use null target_id for bulk token)
      const verifyResponse = await axios.post(
        `${API}/admin-ops/2fa/verify`,
        {
          code: bulkModal.twoFaCode,
          action: 'bulk_prompt_delete',
          target_id: null
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      const twoFaToken = verifyResponse.data.token;
      
      let succeeded = 0;
      let failed = 0;
      const failureReasons = [];
      
      // Delete native prompts via native endpoint
      for (const promptId of nativeIds) {
        try {
          await axios.request({
            method: 'DELETE',
            url: `${API}/admin-ops/prompts/${promptId}/live`,
            headers: {
              ...getAuthHeaders(),
              'X-2FA-Token': twoFaToken,
              'Content-Type': 'application/json'
            },
            data: {
              reason: bulkModal.reason
            },
            withCredentials: true
          });
          succeeded++;
        } catch (err) {
          console.error(`Failed to delete native prompt ${promptId}:`, err);
          const detail = err.response?.data?.detail;
          const prompt = prompts.find(p => p.id === promptId);
          failureReasons.push({
            name: prompt?.name || promptId,
            reason: typeof detail === 'string' ? detail : (detail?.error || detail?.message || 'Unknown error'),
            source: 'native'
          });
          failed++;
        }
      }
      
      // Delete intake-derived prompts via prompt-references endpoint
      for (const refId of intakeIds) {
        try {
          await axios.request({
            method: 'DELETE',
            url: `${API}/admin-ops/prompt-references/${refId}`,
            headers: {
              ...getAuthHeaders(),
              'X-2FA-Token': twoFaToken,
              'Content-Type': 'application/json'
            },
            data: {
              reason: bulkModal.reason
            },
            withCredentials: true
          });
          succeeded++;
        } catch (err) {
          console.error(`Failed to delete intake prompt reference ${refId}:`, err);
          const detail = err.response?.data?.detail;
          const prompt = prompts.find(p => p.id === refId);
          failureReasons.push({
            name: prompt?.name || refId,
            reason: typeof detail === 'string' ? detail : (detail?.error || detail?.message || 'Unknown error'),
            source: 'intake'
          });
          failed++;
        }
      }
      
      resetBulkModal();
      clearSelection();
      fetchPrompts();
      
      // Show result summary
      const nativeCount = nativeIds.length;
      const intakeCount = intakeIds.length;
      const summaryParts = [];
      if (nativeCount > 0) summaryParts.push(`${nativeCount} native`);
      if (intakeCount > 0) summaryParts.push(`${intakeCount} intake`);
      
      if (failed === 0) {
        toast.success(`Successfully deleted ${succeeded} prompt(s) (${summaryParts.join(', ')})`);
      } else if (succeeded === 0) {
        const reasons = failureReasons.slice(0, 3).map(f => `${f.name}: ${f.reason}`).join(', ');
        toast.error(`Failed to delete all ${failed} prompts. ${reasons}`);
      } else {
        const reasons = failureReasons.slice(0, 2).map(f => `${f.name}: ${f.reason}`).join(', ');
        toast.warning(`Deleted ${succeeded}, failed ${failed}. ${reasons}`);
      }
    } catch (err) {
      console.error('2FA verification failed:', err);
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403) {
        toast.error('Invalid 2FA code. Please try again.');
        setBulkModal(prev => ({ ...prev, processing: false, twoFaCode: '' }));
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to verify 2FA');
        setBulkModal(prev => ({ ...prev, processing: false }));
      }
    }
  };

  // Execute bulk publish (Go Live) - Uses status endpoint for true bulk publish
  const executeBulkPublish = async () => {
    if (!bulkModal.eligibilityInfo || bulkModal.eligibilityInfo.eligible.length === 0) {
      toast.error('No eligible prompts to publish');
      return;
    }
    
    setBulkModal(prev => ({ ...prev, processing: true }));
    
    const eligiblePrompts = bulkModal.eligibilityInfo.eligible;
    let succeeded = 0;
    let failed = 0;
    const failureReasons = [];
    const publishedIds = [];
    const failedIds = [];
    
    // Use the status endpoint for both native and intake prompts
    // This ensures we don't deactivate other prompts of the same task_type
    for (const prompt of eligiblePrompts) {
      try {
        if (prompt.source === 'intake') {
          // For intake-derived prompts
          await axios.patch(
            `${API}/admin-ops/prompt-references/${prompt.id}/status`,
            { status: 'live', source: 'bulk_publish' },
            { headers: getAuthHeaders(), withCredentials: true }
          );
        } else {
          // For native prompts - use the new status endpoint (not activate!)
          await axios.patch(
            `${API}/admin-ops/prompts/${prompt.id}/status`,
            { status: 'live', source: 'bulk_publish' },
            { headers: getAuthHeaders(), withCredentials: true }
          );
        }
        succeeded++;
        publishedIds.push(prompt.id);
      } catch (err) {
        console.error(`Failed to publish prompt ${prompt.id}:`, err);
        failed++;
        failedIds.push(prompt.id);
        failureReasons.push({
          id: prompt.id,
          name: prompt.name,
          reason: err.response?.data?.detail || 'Unknown error'
        });
      }
    }
    
    // Log results for debugging
    console.log('[Bulk Publish Result]', {
      attempted: eligiblePrompts.length,
      succeeded,
      failed,
      publishedIds,
      failedIds,
      failureReasons
    });
    
    resetBulkModal();
    clearSelection();
    
    // Refresh prompts to get accurate state from backend
    await fetchPrompts();
    
    const skippedCount = bulkModal.eligibilityInfo.ineligible.length;
    
    // Build accurate status message
    if (failed === 0 && succeeded > 0) {
      const msg = skippedCount > 0 
        ? `Published ${succeeded} prompt(s) to live. ${skippedCount} ineligible (skipped).`
        : `Successfully published ${succeeded} prompt(s) to live!`;
      toast.success(msg);
    } else if (succeeded === 0) {
      const reasons = failureReasons.slice(0, 3).map(r => `${r.name}: ${r.reason}`).join('; ');
      toast.error(`Failed to publish all ${failed} prompts. ${reasons}`);
    } else {
      // Mixed result
      const reasons = failureReasons.slice(0, 2).map(r => r.reason).join(', ');
      toast.warning(`Published ${succeeded}, failed ${failed}. Issues: ${reasons}`);
    }
  };

  // Execute bulk unpublish (Move to Draft) - SOURCE-AWARE ROUTING
  const executeBulkUnpublish = async () => {
    if (!bulkModal.eligibilityInfo || bulkModal.eligibilityInfo.eligible.length === 0) {
      toast.error('No eligible prompts to unpublish');
      return;
    }
    
    setBulkModal(prev => ({ ...prev, processing: true }));
    
    const eligiblePrompts = bulkModal.eligibilityInfo.eligible;
    let succeeded = 0;
    let failed = 0;
    
    for (const prompt of eligiblePrompts) {
      try {
        if (prompt.source === 'intake') {
          // For intake-derived prompts, update status to draft
          await axios.patch(
            `${API}/admin-ops/prompt-references/${prompt.id}/status`,
            { status: 'draft' },
            { headers: getAuthHeaders(), withCredentials: true }
          );
        } else {
          // For native prompts, deactivate
          await axios.patch(
            `${API}/admin-ops/prompts/${prompt.id}`,
            { status: 'draft', is_active: false },
            { headers: getAuthHeaders(), withCredentials: true }
          );
        }
        succeeded++;
      } catch (err) {
        console.error(`Failed to unpublish prompt ${prompt.id}:`, err);
        failed++;
      }
    }
    
    resetBulkModal();
    clearSelection();
    fetchPrompts();
    
    if (failed === 0) {
      toast.success(`Successfully moved ${succeeded} prompt(s) to draft`);
    } else {
      toast.warning(`Unpublished ${succeeded}, failed ${failed}`);
    }
  };

  // Copy prompt to clipboard
  const handleCopy = (prompt) => {
    const text = prompt.content || prompt.prompt_text || '';
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Prompt copied to clipboard');
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  // Open view modal
  const handleView = (prompt) => {
    setViewModal({ open: true, prompt });
  };

  // Open edit modal - Updated with metadata fields
  const handleEdit = (prompt) => {
    setEditForm({
      name: prompt.name || '',
      category: prompt.category || 'letter_generation',
      family: prompt.family || 'isolated',
      method: prompt.method || '',
      round: prompt.round || 'Round 1',
      content: prompt.content || prompt.prompt_text || '',
      notes: prompt.notes || ''
    });
    setEditModal({ open: true, prompt, saving: false });
  };

  // Save edit - Updated with metadata fields
  const handleSaveEdit = async () => {
    if (!editModal.prompt) return;
    
    setEditModal(prev => ({ ...prev, saving: true }));
    try {
      await axios.patch(
        `${API}/admin-ops/prompts/${editModal.prompt.id}`,
        {
          name: editForm.name,
          category: editForm.category,
          family: editForm.family,
          method: editForm.method || null,
          round: editForm.round,
          content: editForm.content,
          notes: editForm.notes
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      toast.success('Prompt updated successfully');
      setEditModal({ open: false, prompt: null, saving: false });
      fetchPrompts();
    } catch (err) {
      console.error('Failed to update prompt:', err);
      toast.error(err.response?.data?.detail || 'Failed to update prompt');
      setEditModal(prev => ({ ...prev, saving: false }));
    }
  };

  // Open delete modal - simple flow for all prompts
  const handleDelete = (prompt) => {
    setDeleteModal({ 
      open: true, 
      prompt, 
      deleting: false,
      reason: '',
      twoFaCode: ''
    });
  };

  // Reset delete modal
  const resetDeleteModal = () => {
    setDeleteModal({ 
      open: false, 
      prompt: null, 
      deleting: false,
      reason: '',
      twoFaCode: ''
    });
  };

  // Confirm delete - single flow: reason + 2FA for all prompts (SOURCE-AWARE)
  const handleConfirmDelete = async () => {
    if (!deleteModal.prompt) return;
    
    // Validate inputs
    if (!deleteModal.reason || deleteModal.reason.trim().length < 5) {
      toast.error('Please provide a reason (minimum 5 characters)');
      return;
    }
    
    if (!deleteModal.twoFaCode || deleteModal.twoFaCode.length !== 6) {
      toast.error('Please enter a valid 6-digit 2FA code');
      return;
    }
    
    setDeleteModal(prev => ({ ...prev, deleting: true }));
    
    const isIntakePrompt = deleteModal.prompt.source === 'intake';
    
    try {
      // First verify 2FA
      const verifyResponse = await axios.post(
        `${API}/admin-ops/2fa/verify`,
        {
          code: deleteModal.twoFaCode,
          action: 'prompt_delete',
          target_id: deleteModal.prompt.id
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      const twoFaToken = verifyResponse.data.token;
      
      // Route to correct endpoint based on source
      if (isIntakePrompt) {
        // Delete intake-derived prompt via prompt-references endpoint
        await axios.request({
          method: 'DELETE',
          url: `${API}/admin-ops/prompt-references/${deleteModal.prompt.id}`,
          headers: {
            ...getAuthHeaders(),
            'X-2FA-Token': twoFaToken,
            'Content-Type': 'application/json'
          },
          data: {
            reason: deleteModal.reason
          },
          withCredentials: true
        });
      } else {
        // Delete native prompt via native endpoint
        await axios.request({
          method: 'DELETE',
          url: `${API}/admin-ops/prompts/${deleteModal.prompt.id}/live`,
          headers: {
            ...getAuthHeaders(),
            'X-2FA-Token': twoFaToken,
            'Content-Type': 'application/json'
          },
          data: {
            reason: deleteModal.reason
          },
          withCredentials: true
        });
      }
      
      toast.success(`${isIntakePrompt ? 'Linked prompt reference' : 'Prompt'} archived successfully`);
      resetDeleteModal();
      fetchPrompts();
    } catch (err) {
      console.error('Failed to archive prompt:', err);
      const detail = err.response?.data?.detail;
      
      if (err.response?.status === 403) {
        const errorMsg = typeof detail === 'object' ? detail?.error : detail;
        toast.error(errorMsg || 'Invalid 2FA code. Please try again.');
        setDeleteModal(prev => ({ ...prev, deleting: false, twoFaCode: '' }));
        return;
      }
      
      toast.error(typeof detail === 'string' ? detail : detail?.message || detail?.error || 'Failed to archive prompt');
      setDeleteModal(prev => ({ ...prev, deleting: false }));
    }
  };

  // Create new prompt - Updated with metadata fields
  const handleCreate = async () => {
    if (!createForm.name.trim() || !createForm.content.trim()) {
      toast.error('Name and content are required');
      return;
    }
    
    setCreateModal(prev => ({ ...prev, saving: true }));
    try {
      await axios.post(
        `${API}/admin-ops/prompts`,
        {
          name: createForm.name,
          category: createForm.category,
          family: createForm.family,
          method: createForm.method || null,
          round: createForm.round,
          module: 'dispute', // Default module for letter generation
          content: createForm.content,
          notes: createForm.notes,
          status: 'draft'
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      toast.success('Prompt created successfully');
      setCreateModal({ open: false, saving: false });
      setCreateForm({ 
        name: '', 
        category: 'letter_generation', 
        family: 'isolated',
        method: '',
        round: 'Round 1',
        content: '', 
        notes: '' 
      });
      fetchPrompts();
    } catch (err) {
      console.error('Failed to create prompt:', err);
      toast.error(err.response?.data?.detail || 'Failed to create prompt');
      setCreateModal(prev => ({ ...prev, saving: false }));
    }
  };

  // Access check
  if (!['admin', 'specialist', 'platform_owner', 'platform_admin', 'owner'].includes(user?.role)) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <AlertCircle size={48} className="text-red-500 mb-4" />
        <h2 className="text-xl font-bold text-white mb-2">Admin Access Required</h2>
        <p className="text-slate-400">This page is only available to administrators.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="prompt-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Terminal className="text-amber-400" />
            Prompt Management
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Manage AI prompts and configurations
          </p>
        </div>
        <Button 
          onClick={() => setCreateModal({ open: true, saving: false })}
          className="bg-amber-600 hover:bg-amber-700"
          data-testid="create-prompt-btn"
        >
          <Plus size={16} className="mr-2" />
          New Prompt
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-stone-800/50 border border-slate-700 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-amber-400">{counts.total}</p>
          <p className="text-xs text-slate-400">Total Prompts</p>
        </div>
        <div className="bg-stone-800/50 border border-slate-700 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-emerald-400">
            {prompts.filter(p => p.status === 'live' || p.is_active).length}
          </p>
          <p className="text-xs text-slate-400">Live</p>
        </div>
        <div className="bg-stone-800/50 border border-slate-700 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-slate-300">{counts.native}</p>
          <p className="text-xs text-slate-400">Native</p>
        </div>
        <div className="bg-stone-800/50 border border-slate-700 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-amber-400">{counts.intake_derived}</p>
          <p className="text-xs text-slate-400">From Intake</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-stone-800/50 border border-slate-700 rounded-lg p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search prompts, methods..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-stone-900/50 border-slate-600 text-white"
                data-testid="search-input"
              />
            </div>
          </div>
          
          {/* Category Filter (New Primary Filter) */}
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[170px] bg-stone-900/50 border-slate-600 text-white" data-testid="category-filter">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORY_OPTIONS.map(c => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Family Filter (New Primary Filter) */}
          <Select value={familyFilter} onValueChange={setFamilyFilter}>
            <SelectTrigger className="w-[180px] bg-stone-900/50 border-slate-600 text-white" data-testid="family-filter">
              <SelectValue placeholder="Family" />
            </SelectTrigger>
            <SelectContent>
              {FAMILY_OPTIONS.map(f => (
                <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Round Filter (New Primary Filter) */}
          <Select value={roundFilter} onValueChange={setRoundFilter}>
            <SelectTrigger className="w-[140px] bg-stone-900/50 border-slate-600 text-white" data-testid="round-filter">
              <SelectValue placeholder="Round" />
            </SelectTrigger>
            <SelectContent>
              {ROUND_OPTIONS.map(r => (
                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Status Filter */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[130px] bg-stone-900/50 border-slate-600 text-white" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* More Filters Toggle */}
          <Button 
            variant="outline" 
            onClick={() => setShowMoreFilters(!showMoreFilters)}
            className={`border-slate-600 text-slate-300 hover:bg-slate-700 ${showMoreFilters ? 'bg-slate-700' : ''}`}
            data-testid="more-filters-btn"
          >
            <Filter size={14} className="mr-2" />
            More
            <ChevronDown size={14} className={`ml-1 transition-transform ${showMoreFilters ? 'rotate-180' : ''}`} />
          </Button>
          
          {/* Refresh */}
          <Button 
            variant="outline" 
            onClick={fetchPrompts}
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
            data-testid="refresh-btn"
          >
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
        </div>
        
        {/* More Filters Row (Source, Method) */}
        {showMoreFilters && (
          <div className="mt-3 pt-3 border-t border-slate-700/50 flex items-center gap-4">
            {/* Source Filter (Moved to More Filters) */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400">Source:</span>
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className="w-[150px] bg-stone-900/50 border-slate-600 text-white h-8 text-sm">
                  <SelectValue placeholder="Source" />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_OPTIONS.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        
        {/* Active filter count */}
        {(searchQuery || categoryFilter !== 'all' || familyFilter !== 'all' || roundFilter !== 'all' || statusFilter !== 'all' || sourceFilter !== 'all') && (
          <div className="mt-3 flex items-center gap-2 text-sm text-slate-400">
            <Filter size={14} />
            <span>Showing {filteredPrompts.length} of {prompts.length} prompts</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchQuery('');
                setCategoryFilter('all');
                setFamilyFilter('all');
                setRoundFilter('all');
                setStatusFilter('all');
                setSourceFilter('all');
              }}
              className="text-amber-400 hover:text-amber-300 h-6 px-2"
            >
              Clear filters
            </Button>
          </div>
        )}
      </div>

      {/* Selection Bar - Shows when prompts are loaded */}
      {!loading && !error && filteredPrompts.length > 0 && (
        <div className="bg-stone-800/50 border border-slate-700 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Select All Checkbox */}
            <div className="flex items-center gap-2">
              <Checkbox
                checked={allVisibleSelected}
                onCheckedChange={handleSelectAll}
                className="border-slate-500 data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
                data-testid="select-all-checkbox"
                ref={(el) => {
                  if (el) {
                    // Set indeterminate state
                    const input = el.querySelector('input') || el;
                    if (input && 'indeterminate' in input) {
                      input.indeterminate = someVisibleSelected && !allVisibleSelected;
                    }
                  }
                }}
              />
              <span className="text-sm text-slate-300">
                {allVisibleSelected ? 'Deselect All' : 'Select All'}
              </span>
            </div>
            
            {/* Selected count */}
            {selectedCount > 0 && (
              <div className="flex items-center gap-2 text-sm text-amber-400">
                <CheckSquare size={14} />
                <span>{selectedCount} selected</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSelection}
                  className="h-6 px-2 text-slate-400 hover:text-white"
                >
                  <X size={12} className="mr-1" />
                  Clear
                </Button>
              </div>
            )}
          </div>
          
          {/* Actions Dropdown - Dynamic based on selection status */}
          {selectedCount > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="bulk-actions-btn"
                >
                  <MoreVertical size={16} className="mr-2" />
                  Actions ({selectedCount})
                  <ChevronDown size={14} className="ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-stone-800 border-slate-700 min-w-[200px]">
                {/* Go Live Selected - show when drafts are selected */}
                {(() => {
                  const breakdown = getSelectionStatusBreakdown();
                  const hasPublishable = breakdown.draft.length > 0;
                  const hasLive = breakdown.live.length > 0;
                  
                  return (
                    <>
                      {hasPublishable && (
                        <>
                          <DropdownMenuItem 
                            onClick={() => handleBulkAction('publish')}
                            className="text-emerald-400 hover:bg-emerald-500/10 cursor-pointer"
                            data-testid="bulk-publish-option"
                          >
                            <Rocket size={14} className="mr-2" />
                            Go Live Selected
                            {breakdown.live.length > 0 && (
                              <span className="ml-auto text-xs text-slate-500">
                                ({breakdown.draft.length} eligible)
                              </span>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-slate-700" />
                        </>
                      )}
                      
                      {hasLive && (
                        <>
                          <DropdownMenuItem 
                            onClick={() => handleBulkAction('unpublish')}
                            className="text-amber-400 hover:bg-amber-500/10 cursor-pointer"
                            data-testid="bulk-unpublish-option"
                          >
                            <ArrowDownCircle size={14} className="mr-2" />
                            Move to Draft
                            {breakdown.draft.length > 0 && (
                              <span className="ml-auto text-xs text-slate-500">
                                ({breakdown.live.length} live)
                              </span>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-slate-700" />
                        </>
                      )}
                    </>
                  );
                })()}
                
                <DropdownMenuItem 
                  onClick={() => handleBulkAction('archive')}
                  className="text-slate-300 hover:bg-slate-700 cursor-pointer"
                  data-testid="bulk-archive-option"
                >
                  <Archive size={14} className="mr-2 text-amber-400" />
                  Archive Selected
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-700" />
                <DropdownMenuItem 
                  onClick={() => handleBulkAction('delete')}
                  className="text-red-400 hover:bg-red-500/10 cursor-pointer"
                  data-testid="bulk-delete-option"
                >
                  <Trash2 size={14} className="mr-2" />
                  Delete Selected (2FA)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}

      {/* Prompt Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-48 bg-stone-800/50" />
          ))}
        </div>
      ) : error ? (
        <div className="text-center py-12 bg-stone-800/30 rounded-lg border border-slate-700">
          <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Failed to Load</h3>
          <p className="text-slate-400 mb-4">{error}</p>
          <Button onClick={fetchPrompts} variant="outline">
            <RefreshCw size={16} className="mr-2" />
            Retry
          </Button>
        </div>
      ) : filteredPrompts.length === 0 ? (
        <div className="text-center py-12 bg-stone-800/30 rounded-lg border border-slate-700">
          <Terminal size={48} className="mx-auto text-slate-500 mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No Prompts Found</h3>
          <p className="text-slate-400">
            {prompts.length === 0 
              ? 'Create your first prompt to get started'
              : 'Try adjusting your filters'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPrompts.map(prompt => (
            <PromptCard
              key={prompt.id}
              prompt={prompt}
              onView={handleView}
              onEdit={handleEdit}
              onCopy={handleCopy}
              onDelete={handleDelete}
              isSelected={selectedIds.has(prompt.id)}
              onSelect={handleToggleSelect}
            />
          ))}
        </div>
      )}

      {/* View Modal */}
      <Dialog open={viewModal.open} onOpenChange={(open) => setViewModal({ open, prompt: open ? viewModal.prompt : null })}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col bg-stone-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Eye className="text-amber-400" />
              {viewModal.prompt?.name || 'View Prompt'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {viewModal.prompt?.source === 'intake' ? 'Linked from Training Intake' : 'Native Prompt'}
              {' · '}
              {TASK_TYPES.find(t => t.value === (viewModal.prompt?.task_type || viewModal.prompt?.module))?.label || viewModal.prompt?.task_type}
              {' · '}
              v{viewModal.prompt?.version || 1}
            </DialogDescription>
          </DialogHeader>
          
          {viewModal.prompt && (
            <div className="flex-1 overflow-y-auto space-y-4 py-4">
              {/* Source info for intake */}
              {viewModal.prompt.source === 'intake' && viewModal.prompt.source_intake_title && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                  <p className="text-sm text-amber-400 flex items-center gap-2">
                    <Link2 size={14} />
                    <span>Linked from: {viewModal.prompt.source_intake_title}</span>
                  </p>
                </div>
              )}
              
              {/* Prompt Content */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-slate-300">Prompt Content</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopy(viewModal.prompt)}
                    className="text-amber-400 hover:text-amber-300"
                  >
                    <Copy size={14} className="mr-1" />
                    Copy
                  </Button>
                </div>
                <div className="bg-stone-800 border border-slate-700 rounded-lg p-4 max-h-[300px] overflow-y-auto">
                  <pre className="text-sm text-slate-300 whitespace-pre-wrap font-mono">
                    {viewModal.prompt.content || viewModal.prompt.prompt_text || 'No content'}
                  </pre>
                </div>
              </div>
              
              {/* Notes */}
              {viewModal.prompt.notes && (
                <div>
                  <Label className="text-slate-300 mb-2 block">Notes</Label>
                  <div className="bg-stone-800 border border-slate-700 rounded-lg p-3">
                    <p className="text-sm text-slate-400">{viewModal.prompt.notes}</p>
                  </div>
                </div>
              )}
              
              {/* Metadata */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Status:</span>
                  <span className="text-slate-300 ml-2">{viewModal.prompt.status || 'draft'}</span>
                </div>
                <div>
                  <span className="text-slate-500">Source:</span>
                  <span className="text-slate-300 ml-2">{viewModal.prompt.source || 'native'}</span>
                </div>
                <div>
                  <span className="text-slate-500">Created:</span>
                  <span className="text-slate-300 ml-2">
                    {viewModal.prompt.created_at ? new Date(viewModal.prompt.created_at).toLocaleString() : 'Unknown'}
                  </span>
                </div>
                {viewModal.prompt.word_count && (
                  <div>
                    <span className="text-slate-500">Words:</span>
                    <span className="text-slate-300 ml-2">{viewModal.prompt.word_count}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="border-t border-slate-700 pt-4">
            <Button variant="outline" onClick={() => setViewModal({ open: false, prompt: null })} className="border-slate-600 text-slate-300">
              Close
            </Button>
            {viewModal.prompt && viewModal.prompt.source !== 'intake' && (
              <Button onClick={() => { setViewModal({ open: false, prompt: null }); handleEdit(viewModal.prompt); }} className="bg-amber-600 hover:bg-amber-700">
                <Edit3 size={14} className="mr-2" />
                Edit
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal - Updated with Metadata Fields */}
      <Dialog open={editModal.open} onOpenChange={(open) => !editModal.saving && setEditModal({ open, prompt: open ? editModal.prompt : null, saving: false })}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-stone-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit3 className="text-amber-400" />
              Edit Prompt
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Update prompt metadata and content. Metadata is stored separately and never sent to the LLM.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Name */}
            <div>
              <Label className="text-slate-300 mb-2 block">Name *</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                className="bg-stone-800 border-slate-600 text-white"
                placeholder="Prompt name"
                data-testid="edit-name-input"
              />
            </div>
            
            {/* Metadata Row - Category, Family, Round, Method */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Category */}
              <div>
                <Label className="text-slate-300 mb-2 block text-sm">Category</Label>
                <Select value={editForm.category} onValueChange={(v) => setEditForm(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger className="bg-stone-800 border-slate-600 text-white h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.filter(c => c.value !== 'all').map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Family */}
              <div>
                <Label className="text-slate-300 mb-2 block text-sm">Family</Label>
                <Select value={editForm.family} onValueChange={(v) => setEditForm(prev => ({ ...prev, family: v }))}>
                  <SelectTrigger className="bg-stone-800 border-slate-600 text-white h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FAMILY_OPTIONS.filter(f => f.value !== 'all').map(f => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Round */}
              <div>
                <Label className="text-slate-300 mb-2 block text-sm">Round</Label>
                <Select value={editForm.round} onValueChange={(v) => setEditForm(prev => ({ ...prev, round: v }))}>
                  <SelectTrigger className="bg-stone-800 border-slate-600 text-white h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROUND_OPTIONS.filter(r => r.value !== 'all').map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Method (Text Input) */}
              <div>
                <Label className="text-slate-300 mb-2 block text-sm">Method</Label>
                <Input
                  value={editForm.method}
                  onChange={(e) => setEditForm(prev => ({ ...prev, method: e.target.value }))}
                  className="bg-stone-800 border-slate-600 text-white h-9 text-sm"
                  placeholder="e.g., round1_structural"
                  list="method-suggestions"
                  data-testid="edit-method-input"
                />
                <datalist id="method-suggestions">
                  {METHOD_SUGGESTIONS.map(m => (
                    <option key={m} value={m} />
                  ))}
                </datalist>
              </div>
            </div>
            
            {/* Prompt Body */}
            <div>
              <Label className="text-slate-300 mb-2 block">Prompt Body *</Label>
              <p className="text-xs text-slate-500 mb-2">
                AI instructions only. Do NOT include PROMPT NAME, CATEGORY, FAMILY, METHOD, or ROUND headers.
              </p>
              <Textarea
                value={editForm.content}
                onChange={(e) => setEditForm(prev => ({ ...prev, content: e.target.value }))}
                className="bg-stone-800 border-slate-600 text-white min-h-[200px] font-mono text-sm"
                placeholder="You are drafting a formal dispute letter..."
                data-testid="edit-content-input"
              />
            </div>
            
            {/* Notes */}
            <div>
              <Label className="text-slate-300 mb-2 block">Notes (optional)</Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-stone-800 border-slate-600 text-white"
                placeholder="Internal notes - not sent to LLM..."
                rows={2}
                data-testid="edit-notes-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setEditModal({ open: false, prompt: null, saving: false })}
              disabled={editModal.saving}
              className="border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveEdit}
              disabled={editModal.saving}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="save-edit-btn"
            >
              {editModal.saving ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={14} className="mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Modal - Simple flow: Reason + 2FA for ALL prompts */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => !deleteModal.deleting && resetDeleteModal()}>
        <DialogContent className="max-w-md bg-stone-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Trash2 className="text-red-400" />
              Archive Prompt
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This action requires 2FA verification.
            </DialogDescription>
          </DialogHeader>
          
          {deleteModal.prompt && (
            <div className="py-4 space-y-4">
              {/* Prompt info */}
              <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-slate-300 font-medium">
                    {deleteModal.prompt.name}
                  </p>
                  {(deleteModal.prompt.is_active || deleteModal.prompt.status === 'live') && (
                    <span className="px-2 py-0.5 text-xs bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded">
                      Active
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-400">
                  This action will archive the prompt. It can be restored later if needed.
                </p>
              </div>
              
              {/* Reason field */}
              <div>
                <Label className="text-slate-300 mb-2 block">Reason for Archiving *</Label>
                <Textarea
                  value={deleteModal.reason}
                  onChange={(e) => setDeleteModal(prev => ({ ...prev, reason: e.target.value }))}
                  className="bg-stone-800 border-slate-600 text-white"
                  placeholder="Why is this prompt being archived? (min 5 characters)"
                  rows={2}
                  data-testid="delete-reason-input"
                />
              </div>
              
              {/* 2FA Code field */}
              <div>
                <Label className="text-slate-300 mb-2 block">2FA Verification Code *</Label>
                <Input
                  value={deleteModal.twoFaCode}
                  onChange={(e) => setDeleteModal(prev => ({ ...prev, twoFaCode: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                  className="bg-stone-800 border-slate-600 text-white text-center text-lg tracking-widest"
                  placeholder="000000"
                  maxLength={6}
                  data-testid="delete-2fa-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Enter the 6-digit code from your authenticator app.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={resetDeleteModal}
              disabled={deleteModal.deleting}
              className="border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmDelete}
              disabled={deleteModal.deleting || deleteModal.reason.length < 5 || deleteModal.twoFaCode.length !== 6}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {deleteModal.deleting ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  Archiving...
                </>
              ) : (
                <>
                  <Trash2 size={14} className="mr-2" />
                  Archive
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Modal - Updated with Metadata Fields */}
      <Dialog open={createModal.open} onOpenChange={(open) => !createModal.saving && setCreateModal({ open, saving: false })}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-stone-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="text-amber-400" />
              Create New Prompt
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Add a new prompt with dedicated metadata fields. Metadata is stored separately and never sent to the LLM.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Name */}
            <div>
              <Label className="text-slate-300 mb-2 block">Name *</Label>
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                className="bg-stone-800 border-slate-600 text-white"
                placeholder="e.g., Round 1 – Structural + Recovery Factual Dispute"
                data-testid="create-name-input"
              />
            </div>
            
            {/* Metadata Row - Category, Family, Round, Method */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Category */}
              <div>
                <Label className="text-slate-300 mb-2 block text-sm">Category</Label>
                <Select value={createForm.category} onValueChange={(v) => setCreateForm(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger className="bg-stone-800 border-slate-600 text-white h-9" data-testid="create-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.filter(c => c.value !== 'all').map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Family */}
              <div>
                <Label className="text-slate-300 mb-2 block text-sm">Family</Label>
                <Select value={createForm.family} onValueChange={(v) => setCreateForm(prev => ({ ...prev, family: v }))}>
                  <SelectTrigger className="bg-stone-800 border-slate-600 text-white h-9" data-testid="create-family-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FAMILY_OPTIONS.filter(f => f.value !== 'all').map(f => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Round */}
              <div>
                <Label className="text-slate-300 mb-2 block text-sm">Round</Label>
                <Select value={createForm.round} onValueChange={(v) => setCreateForm(prev => ({ ...prev, round: v }))}>
                  <SelectTrigger className="bg-stone-800 border-slate-600 text-white h-9" data-testid="create-round-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROUND_OPTIONS.filter(r => r.value !== 'all').map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Method (Text Input with Suggestions) */}
              <div>
                <Label className="text-slate-300 mb-2 block text-sm">Method</Label>
                <Input
                  value={createForm.method}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, method: e.target.value }))}
                  className="bg-stone-800 border-slate-600 text-white h-9 text-sm"
                  placeholder="e.g., round1_combo"
                  list="create-method-suggestions"
                  data-testid="create-method-input"
                />
                <datalist id="create-method-suggestions">
                  {METHOD_SUGGESTIONS.map(m => (
                    <option key={m} value={m} />
                  ))}
                </datalist>
              </div>
            </div>
            
            {/* Prompt Body */}
            <div>
              <Label className="text-slate-300 mb-2 block">Prompt Body *</Label>
              <p className="text-xs text-slate-500 mb-2">
                AI instructions only. Do NOT include metadata headers like PROMPT NAME, CATEGORY, etc.
              </p>
              <Textarea
                value={createForm.content}
                onChange={(e) => setCreateForm(prev => ({ ...prev, content: e.target.value }))}
                className="bg-stone-800 border-slate-600 text-white min-h-[200px] font-mono text-sm"
                placeholder="You are drafting a formal dispute letter for a consumer disputing..."
                data-testid="create-content-input"
              />
            </div>
            
            {/* Notes */}
            <div>
              <Label className="text-slate-300 mb-2 block">Notes (optional)</Label>
              <Textarea
                value={createForm.notes}
                onChange={(e) => setCreateForm(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-stone-800 border-slate-600 text-white"
                placeholder="Internal admin notes - not sent to LLM..."
                rows={2}
                data-testid="create-notes-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => { 
                setCreateModal({ open: false, saving: false }); 
                setCreateForm({ 
                  name: '', 
                  category: 'letter_generation', 
                  family: 'isolated',
                  method: '',
                  round: 'Round 1',
                  content: '', 
                  notes: '' 
                }); 
              }}
              disabled={createModal.saving}
              className="border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreate}
              disabled={createModal.saving || !createForm.name.trim() || !createForm.content.trim()}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="create-submit-btn"
            >
              {createModal.saving ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus size={14} className="mr-2" />
                  Create Prompt
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Modal */}
      <Dialog open={bulkModal.open} onOpenChange={(open) => !bulkModal.processing && resetBulkModal()}>
        <DialogContent className="max-w-md bg-stone-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {bulkModal.action === 'archive' ? (
                <>
                  <Archive className="text-amber-400" />
                  Bulk Archive Prompts
                </>
              ) : bulkModal.action === 'delete' ? (
                <>
                  <Trash2 className="text-red-400" />
                  Bulk Delete Prompts
                </>
              ) : bulkModal.action === 'publish' ? (
                <>
                  <Rocket className="text-emerald-400" />
                  Publish Prompts Live
                </>
              ) : bulkModal.action === 'unpublish' ? (
                <>
                  <ArrowDownCircle className="text-amber-400" />
                  Move Prompts to Draft
                </>
              ) : null}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {bulkModal.action === 'archive' 
                ? `Archive ${selectedCount} selected prompt(s)?`
                : bulkModal.action === 'delete'
                ? `Delete ${selectedCount} selected prompt(s)? This action requires 2FA.`
                : bulkModal.action === 'publish'
                ? `Publish ${bulkModal.eligibilityInfo?.eligible.length || 0} prompt(s) to live?`
                : bulkModal.action === 'unpublish'
                ? `Move ${bulkModal.eligibilityInfo?.eligible.length || 0} prompt(s) to draft?`
                : ''}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Publish action - show eligibility summary with production warning */}
            {bulkModal.action === 'publish' && bulkModal.eligibilityInfo && (
              <>
                {/* Production Impact Warning */}
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="text-amber-400 mt-0.5 flex-shrink-0" size={16} />
                    <div className="text-sm">
                      <p className="text-amber-300 font-medium">Production Impact Warning</p>
                      <p className="text-slate-400 mt-1">
                        Publishing these prompts may affect production AI decisions and generation output. 
                        This action is audited.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Eligibility Summary */}
                <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-300">Eligibility Summary</span>
                    <div className="flex gap-2">
                      <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-xs">
                        {bulkModal.eligibilityInfo.eligible.length} eligible
                      </span>
                      {bulkModal.eligibilityInfo.ineligible.length > 0 && (
                        <span className="px-2 py-0.5 bg-slate-500/30 text-slate-400 rounded text-xs">
                          {bulkModal.eligibilityInfo.ineligible.length} skipped
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Eligible prompts */}
                  <div className="max-h-24 overflow-y-auto space-y-1 mb-2">
                    {bulkModal.eligibilityInfo.eligible.slice(0, 8).map(prompt => (
                      <div key={prompt.id} className="text-xs text-slate-400 flex items-center gap-2">
                        <Check size={12} className="text-emerald-400 flex-shrink-0" />
                        <span className="truncate">{prompt.name}</span>
                        <span className="text-slate-600 ml-auto">Draft → Live</span>
                      </div>
                    ))}
                    {bulkModal.eligibilityInfo.eligible.length > 8 && (
                      <p className="text-xs text-slate-500">...and {bulkModal.eligibilityInfo.eligible.length - 8} more</p>
                    )}
                  </div>
                  
                  {/* Ineligible prompts */}
                  {bulkModal.eligibilityInfo.ineligible.length > 0 && (
                    <div className="border-t border-slate-600 pt-2 mt-2">
                      <p className="text-xs text-slate-500 mb-1">Skipped (not eligible):</p>
                      <div className="max-h-16 overflow-y-auto space-y-1">
                        {bulkModal.eligibilityInfo.ineligible.slice(0, 5).map(prompt => (
                          <div key={prompt.id} className="text-xs text-slate-500 flex items-center gap-2">
                            <X size={10} className="text-slate-600 flex-shrink-0" />
                            <span className="truncate">{prompt.name}</span>
                            <span className="text-slate-600 ml-auto text-[10px]">
                              {bulkModal.eligibilityInfo.reasons[prompt.id]}
                            </span>
                          </div>
                        ))}
                        {bulkModal.eligibilityInfo.ineligible.length > 5 && (
                          <p className="text-xs text-slate-600">...and {bulkModal.eligibilityInfo.ineligible.length - 5} more</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
            
            {/* Unpublish action - show live prompts that will be moved to draft */}
            {bulkModal.action === 'unpublish' && bulkModal.eligibilityInfo && (
              <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-3">
                <p className="text-sm text-slate-300 mb-2">
                  Moving {bulkModal.eligibilityInfo.eligible.length} live prompt(s) to draft:
                </p>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {bulkModal.eligibilityInfo.eligible.slice(0, 10).map(prompt => (
                    <div key={prompt.id} className="text-xs text-slate-400 flex items-center gap-2">
                      <span className="truncate flex-1">{prompt.name}</span>
                      <span className="text-amber-400">Live → Draft</span>
                    </div>
                  ))}
                  {bulkModal.eligibilityInfo.eligible.length > 10 && (
                    <p className="text-xs text-slate-500 pt-1">...and {bulkModal.eligibilityInfo.eligible.length - 10} more</p>
                  )}
                </div>
                {bulkModal.eligibilityInfo.ineligible.length > 0 && (
                  <p className="text-xs text-slate-500 mt-2">
                    {bulkModal.eligibilityInfo.ineligible.length} prompt(s) skipped (not currently live)
                  </p>
                )}
              </div>
            )}
            
            {/* Archive/Delete - show selected prompts summary */}
            {(bulkModal.action === 'archive' || bulkModal.action === 'delete') && (
              <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-3">
                <p className="text-sm text-slate-300 mb-2">Selected prompts:</p>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {Array.from(selectedIds).slice(0, 10).map(id => {
                    const prompt = prompts.find(p => p.id === id);
                    return prompt ? (
                      <div key={id} className="text-xs text-slate-400 flex items-center gap-2">
                        <span className="truncate flex-1">{prompt.name}</span>
                        {(prompt.is_active || prompt.status === 'live') && (
                          <span className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-[10px]">
                            Live
                          </span>
                        )}
                      </div>
                    ) : null;
                  })}
                  {selectedCount > 10 && (
                    <p className="text-xs text-slate-500 pt-1">...and {selectedCount - 10} more</p>
                  )}
                </div>
              </div>
            )}
            
            {/* Delete requires 2FA */}
            {bulkModal.action === 'delete' && (
              <>
                <div>
                  <Label className="text-slate-300 mb-2 block">Reason for Deletion *</Label>
                  <Textarea
                    value={bulkModal.reason}
                    onChange={(e) => setBulkModal(prev => ({ ...prev, reason: e.target.value }))}
                    className="bg-stone-800 border-slate-600 text-white"
                    placeholder="Why are these prompts being deleted? (min 5 characters)"
                    rows={2}
                    data-testid="bulk-delete-reason"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300 mb-2 block">2FA Verification Code *</Label>
                  <Input
                    value={bulkModal.twoFaCode}
                    onChange={(e) => setBulkModal(prev => ({ ...prev, twoFaCode: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                    className="bg-stone-800 border-slate-600 text-white text-center text-lg tracking-widest"
                    placeholder="000000"
                    maxLength={6}
                    data-testid="bulk-delete-2fa"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    One code for the entire batch.
                  </p>
                </div>
              </>
            )}
            
            {/* Archive confirmation */}
            {bulkModal.action === 'archive' && (
              <p className="text-sm text-slate-400">
                This will archive all selected prompts. They can be restored later if needed.
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={resetBulkModal}
              disabled={bulkModal.processing}
              className="border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={
                bulkModal.action === 'archive' ? executeBulkArchive 
                : bulkModal.action === 'delete' ? executeBulkDelete
                : bulkModal.action === 'publish' ? executeBulkPublish
                : bulkModal.action === 'unpublish' ? executeBulkUnpublish
                : () => {}
              }
              disabled={bulkModal.processing || (
                bulkModal.action === 'delete' && 
                (bulkModal.twoFaCode.length !== 6 || bulkModal.reason.length < 5)
              )}
              className={
                bulkModal.action === 'archive' ? 'bg-amber-600 hover:bg-amber-700' 
                : bulkModal.action === 'delete' ? 'bg-red-600 hover:bg-red-700'
                : bulkModal.action === 'publish' ? 'bg-emerald-600 hover:bg-emerald-700'
                : bulkModal.action === 'unpublish' ? 'bg-amber-600 hover:bg-amber-700'
                : 'bg-slate-600'
              }
              data-testid="confirm-bulk-action"
            >
              {bulkModal.processing ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  Processing...
                </>
              ) : bulkModal.action === 'archive' ? (
                <>
                  <Archive size={14} className="mr-2" />
                  Archive {selectedCount} Prompts
                </>
              ) : bulkModal.action === 'delete' ? (
                <>
                  <Trash2 size={14} className="mr-2" />
                  Delete {selectedCount} Prompts
                </>
              ) : bulkModal.action === 'publish' ? (
                <>
                  <Rocket size={14} className="mr-2" />
                  Go Live ({bulkModal.eligibilityInfo?.eligible.length || 0} Prompts)
                </>
              ) : bulkModal.action === 'unpublish' ? (
                <>
                  <ArrowDownCircle size={14} className="mr-2" />
                  Move to Draft ({bulkModal.eligibilityInfo?.eligible.length || 0})
                </>
              ) : null}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PromptManagement;
